import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, HStack, Badge, Divider } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaCar, FaMotorcycle, FaShip, FaTools, FaCheckCircle, FaInfoCircle } from "react-icons/fa";

const FinancePersonalAsset: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/personal-asset"
        fallbackTitle="Personal Asset Management"
        fallbackDescription="Manage and grow your personal assets effectively"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Finance Section
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Personal & Asset Finance Info
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Learn about personal and asset finance</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={16}>
          {/* Introduction */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Heading size="xl" mb={4}>Finance Your Assets with Confidence</Heading>
            <Text fontSize="lg" color="gray.600">
              Whether it's a car, boat, caravan, or equipment, we offer tailored financing solutions
              to help you acquire the assets you need without compromising your cash flow.
            </Text>
          </Box>

          {/* Asset Types Grid */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>What Can You Finance?</Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              <Box bg="blue.50" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor="blue.200" _hover={{ shadow: "lg", transform: "translateY(-2px)", transition: "all 0.3s" }}>
                <Icon as={FaCar} boxSize={12} color="blue.600" mb={3} mx="auto" />
                <Heading size="sm" mb={2} color="blue.800">Motor Vehicles</Heading>
                <Text fontSize="sm" color="gray.600">Cars, trucks, vans, SUVs</Text>
                <Badge colorScheme="blue" mt={2}>From 5.99%</Badge>
              </Box>
              <Box bg="orange.50" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor="orange.200" _hover={{ shadow: "lg", transform: "translateY(-2px)", transition: "all 0.3s" }}>
                <Icon as={FaMotorcycle} boxSize={12} color="orange.600" mb={3} mx="auto" />
                <Heading size="sm" mb={2} color="orange.800">Recreational</Heading>
                <Text fontSize="sm" color="gray.600">Motorcycles, caravans, campers</Text>
                <Badge colorScheme="orange" mt={2}>From 6.49%</Badge>
              </Box>
              <Box bg="cyan.50" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor="cyan.200" _hover={{ shadow: "lg", transform: "translateY(-2px)", transition: "all 0.3s" }}>
                <Icon as={FaShip} boxSize={12} color="cyan.600" mb={3} mx="auto" />
                <Heading size="sm" mb={2} color="cyan.800">Marine</Heading>
                <Text fontSize="sm" color="gray.600">Boats, jet skis, marine equipment</Text>
                <Badge colorScheme="cyan" mt={2}>From 6.99%</Badge>
              </Box>
              <Box bg="purple.50" p={6} borderRadius="lg" textAlign="center" border="2px solid" borderColor="purple.200" _hover={{ shadow: "lg", transform: "translateY(-2px)", transition: "all 0.3s" }}>
                <Icon as={FaTools} boxSize={12} color="purple.600" mb={3} mx="auto" />
                <Heading size="sm" mb={2} color="purple.800">Equipment</Heading>
                <Text fontSize="sm" color="gray.600">Business & personal equipment</Text>
                <Badge colorScheme="purple" mt={2}>From 7.49%</Badge>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Loan Types Comparison */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>Types of Asset Finance</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <Box bg="white" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Badge colorScheme="green" mb={4}>Most Popular</Badge>
                <Heading size="md" mb={4}>Chattel Mortgage</Heading>
                <Text color="gray.600" mb={4}>
                  Ideal for businesses and individuals who want to own the asset. You secure the loan with the asset itself.
                </Text>
                <VStack align="start" spacing={2}>
                  <HStack><Icon as={FaCheckCircle} color="green.500" /><Text fontSize="sm">Claim tax deductions on interest</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="green.500" /><Text fontSize="sm">Ownership from day one</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="green.500" /><Text fontSize="sm">Flexible repayment terms</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="green.500" /><Text fontSize="sm">Balloon payment options</Text></HStack>
                </VStack>
              </Box>

              <Box bg="white" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Badge colorScheme="blue" mb={4}>Business Preferred</Badge>
                <Heading size="md" mb={4}>Commercial Hire Purchase</Heading>
                <Text color="gray.600" mb={4}>
                  Similar to chattel mortgage but with ownership transferring after final payment. Great for businesses.
                </Text>
                <VStack align="start" spacing={2}>
                  <HStack><Icon as={FaCheckCircle} color="blue.500" /><Text fontSize="sm">Tax benefits on repayments</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="blue.500" /><Text fontSize="sm">Fixed interest rates available</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="blue.500" /><Text fontSize="sm">Preserves working capital</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="blue.500" /><Text fontSize="sm">Simple end-of-term transfer</Text></HStack>
                </VStack>
              </Box>

              <Box bg="white" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Badge colorScheme="orange" mb={4}>Flexible</Badge>
                <Heading size="md" mb={4}>Personal Loan</Heading>
                <Text color="gray.600" mb={4}>
                  Unsecured or secured loan for personal use assets. Quick approval and flexible use of funds.
                </Text>
                <VStack align="start" spacing={2}>
                  <HStack><Icon as={FaCheckCircle} color="orange.500" /><Text fontSize="sm">Fast approval process</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="orange.500" /><Text fontSize="sm">No balloon payments</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="orange.500" /><Text fontSize="sm">Fixed or variable rates</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="orange.500" /><Text fontSize="sm">Borrow $5,000 to $100,000</Text></HStack>
                </VStack>
              </Box>

              <Box bg="white" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="md">
                <Badge colorScheme="purple" mb={4}>Business Only</Badge>
                <Heading size="md" mb={4}>Finance Lease</Heading>
                <Text color="gray.600" mb={4}>
                  Lease the asset over a fixed term with option to purchase, return, or refinance at end of lease.
                </Text>
                <VStack align="start" spacing={2}>
                  <HStack><Icon as={FaCheckCircle} color="purple.500" /><Text fontSize="sm">100% tax deductible payments</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="purple.500" /><Text fontSize="sm">Off-balance sheet financing</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="purple.500" /><Text fontSize="sm">No GST upfront</Text></HStack>
                  <HStack><Icon as={FaCheckCircle} color="purple.500" /><Text fontSize="sm">Upgrade flexibility</Text></HStack>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Key Considerations */}
          <Box w="full" bg="gray.50" p={10} borderRadius="xl">
            <HStack mb={6}>
              <Icon as={FaInfoCircle} boxSize={8} color="blue.600" />
              <Heading size="lg">Important Considerations</Heading>
            </HStack>
            <Divider mb={6} />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box>
                <Heading size="sm" mb={2}>Loan Term</Heading>
                <Text fontSize="sm" color="gray.600">Choose between 1-7 years depending on asset type and depreciation</Text>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Deposit Required</Heading>
                <Text fontSize="sm" color="gray.600">Typically 10-20% deposit, though 100% finance may be available</Text>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Interest Rates</Heading>
                <Text fontSize="sm" color="gray.600">Secured loans offer lower rates than unsecured personal loans</Text>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Tax Benefits</Heading>
                <Text fontSize="sm" color="gray.600">Business use may qualify for tax deductions - consult your accountant</Text>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Balloon Payments</Heading>
                <Text fontSize="sm" color="gray.600">Reduce monthly payments with a final balloon payment (typically 20-50%)</Text>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>Insurance</Heading>
                <Text fontSize="sm" color="gray.600">Comprehensive insurance usually required for financed assets</Text>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinancePersonalAsset;
